import { useState } from 'react';
import { useI18n, useT } from '../../i18n';
import { Icon } from '../chrome/Icon';
import { useCancelSubscription, useEntitlements, useMe } from '../../data/queries';
import { TIER_META } from '../../data/entitlements';
import { useBilling } from '../../billing/BillingProvider';

/** Format an ISO-ish UTC datetime ("YYYY-MM-DD HH:MM:SS") as a local date. */
function formatDate(value: string | null, locale: string): string {
  if (!value) return '';
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale || 'en', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

/**
 * Subscription management block in the profile sheet. The whole plan row is
 * tappable (opens the paywall); monthly subscribers also get a cancel control
 * that keeps access until the paid period ends, then falls back to Single Tracker.
 */
export function PlanSection() {
  const t = useT();
  const { locale } = useI18n();
  const ent = useEntitlements();
  const me = useMe();
  const cancel = useCancelSubscription();
  const { openPaywall } = useBilling();
  const [confirming, setConfirming] = useState(false);

  const tier = ent.data?.tier ?? 'free';
  const sub = me.data?.subscription ?? null;
  const tierName = t(TIER_META[tier].nameKey, TIER_META[tier].nameFallback);
  const freeName = t(TIER_META.free.nameKey, TIER_META.free.nameFallback);

  const isFree = tier === 'free';
  const isLifetime = sub?.plan_type === 'lifetime';
  // A lifetime pass shows as e.g. "Family Full Journey".
  const name = isLifetime
    ? t('billing.fjName', '{plan} Full Journey').replace('{plan}', tierName)
    : tierName;
  const isCancelled = sub?.status === 'cancelled';
  const isTrial = sub?.status === 'trialing';
  const periodEnd = formatDate(sub?.current_period_end ?? sub?.trial_end ?? null, locale);
  const canCancel = !isFree && !isLifetime && !isCancelled && sub !== null;

  const statusLine = (() => {
    if (isFree) return t('billing.freePlan', 'Free plan');
    if (isLifetime) {
      return periodEnd
        ? t('billing.oneTimeUntil', 'One-time pass · access until {date}').replace('{date}', periodEnd)
        : t('billing.lifetime', 'One-time pass');
    }
    if (isCancelled) {
      return t('billing.cancelledUntil', 'Access until {date}, then {free}')
        .replace('{date}', periodEnd).replace('{free}', freeName);
    }
    if (isTrial) return t('billing.trialEnds', 'Free trial ends {date}').replace('{date}', periodEnd);
    return t('billing.renewsOn', 'Renews {date}').replace('{date}', periodEnd);
  })();

  // The verb shown on the row (also drives the row's accessible name). Lifetime
  // has nothing to change, so its row isn't actionable.
  const actionLabel = isFree
    ? t('billing.upgrade', 'Upgrade')
    : isCancelled
    ? t('billing.resubscribe', 'Resubscribe')
    : isLifetime
    ? null
    : t('billing.changePlan', 'Change plan');

  const rowInner = (
    <>
      <div className="min-w-0 text-left">
        <span className="block text-[14.5px] font-semibold text-ink">{name}</span>
        <span className="block text-[12px] text-ink-3">{statusLine}</span>
      </div>
      {actionLabel && (
        <span className="shrink-0 inline-flex items-center gap-1 text-[13px] font-semibold text-accent">
          {actionLabel}
          <Icon name="chevron" size={16} />
        </span>
      )}
    </>
  );

  return (
    <section>
      <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('billing.plan', 'Plan')}</h2>

      <div className="rounded-xl border border-line bg-surface px-4 py-3">
        {actionLabel ? (
          <button
            onClick={() => openPaywall(null)}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            {rowInner}
          </button>
        ) : (
          <div className="flex items-center justify-between gap-3">{rowInner}</div>
        )}

        {/* Cancel affordance for monthly subscribers */}
        {canCancel && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            className="mt-3 text-[13px] font-semibold text-ink-3 underline"
          >
            {t('billing.cancelSub', 'Cancel subscription')}
          </button>
        )}

        {canCancel && confirming && (
          <div className="mt-3 rounded-xl bg-grey-soft p-3">
            <p className="text-[12.5px] leading-relaxed text-ink-2">
              {t(
                'billing.cancelExplain',
                "You'll keep {plan} until {date}, then move to {free}. Your data stays — you just can't add beyond the free limits.",
              )
                .replace('{plan}', name)
                .replace('{date}', periodEnd)
                .replace('{free}', freeName)}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={cancel.isPending}
                className="flex-1 min-h-10 rounded-3xl border border-line bg-surface text-[13px] font-semibold text-ink disabled:opacity-50"
              >
                {t('billing.keepPlan', 'Keep it')}
              </button>
              <button
                onClick={() => cancel.mutate(undefined, { onSuccess: () => setConfirming(false) })}
                disabled={cancel.isPending}
                className="flex-1 min-h-10 rounded-3xl bg-[var(--color-amber)] px-4 text-[13px] font-semibold text-[var(--color-amber-ink)] disabled:opacity-50"
              >
                {cancel.isPending ? t('common.loading', 'Loading…') : t('billing.confirmCancel', 'Confirm cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
